import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
  Button,
  Badge,
  IconButton,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { employeeMessageService } from '../services/employeeMessageService';

const drawerWidth = 220;

const NAV_ITEMS = [
  { label: 'Training', path: '/employee/training' },
  { label: 'Sign-Offs', path: '/employee/training/signoffs' },
  { label: 'Message Board', path: '/employee/messages', badgeKey: 'messages' },
  { label: 'Receipts', path: '/employee/receipts' },
  { label: 'Time Off', path: '/employee/time-off' },
  { label: 'Profile', path: '/employee/profile' },
  { label: 'Paystubs', path: '/employee/paystubs' },
];

const EmployeePortalLayout = ({ children, extraNavItems = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const navItems = [...NAV_ITEMS, ...extraNavItems];
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [navOpen, setNavOpen] = useState(true);

  const loadUnreadMessageCount = useCallback(async () => {
    if (!token) {
      setUnreadMessageCount(0);
      return;
    }
    try {
      const res = await employeeMessageService.getMineUnreadCount();
      setUnreadMessageCount(res.count ?? 0);
    } catch {
      /* badge is optional */
    }
  }, [token]);

  useEffect(() => {
    loadUnreadMessageCount();
    if (!token) return undefined;
    const interval = setInterval(loadUnreadMessageCount, 60000);
    return () => clearInterval(interval);
  }, [loadUnreadMessageCount, token]);

  useEffect(() => {
    loadUnreadMessageCount();
  }, [location.pathname, loadUnreadMessageCount]);

  useEffect(() => {
    const onMessagesUpdated = () => loadUnreadMessageCount();
    window.addEventListener('employee-messages-updated', onMessagesUpdated);
    return () => window.removeEventListener('employee-messages-updated', onMessagesUpdated);
  }, [loadUnreadMessageCount]);

  const badgeFor = (item) => {
    if (item.badgeKey === 'messages') return unreadMessageCount;
    return item.badge ?? 0;
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: 48, gap: 1.5 }}>
          <IconButton
            color="inherit"
            aria-label={navOpen ? 'Hide navigation' : 'Show navigation'}
            edge="start"
            size="small"
            onClick={() => setNavOpen((open) => !open)}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontSize: '1rem', flexGrow: 1 }}>
            Employee Portal
          </Typography>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Button color="inherit" size="small" onClick={() => logout()}>
            Log out
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        anchor="left"
        open={navOpen}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: navOpen ? 'block' : 'none',
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: '48px',
            height: 'calc(100vh - 48px)',
            position: 'fixed',
          },
        }}
      >
        <List dense>
          {navItems.map((item) => {
            const badge = badgeFor(item);
            return (
              <ListItemButton
                key={item.path}
                selected={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                onClick={() => navigate(item.path)}
              >
                <ListItemText
                  primary={
                    badge > 0 ? (
                      <Badge
                        badgeContent={badge}
                        color="error"
                        sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', ml: 1 } }}
                      >
                        <span>{item.label}</span>
                      </Badge>
                    ) : (
                      item.label
                    )
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 2, mt: '48px', maxWidth: 960 }}>
        {children}
      </Box>
    </Box>
  );
};

export default EmployeePortalLayout;
